/**
 * Provider Rate Limits API Service
 *
 * Type-safe rate limit management using openapi-fetch typed client.
 * Types derived from provider-rate-limits.openapi.yaml.
 *
 * Endpoints consumed (via provider-rate-limits spec):
 *   GET    /provider-rate-limits                              - List all configs
 *   GET    /provider-rate-limits/{provider}                   - Provider config
 *   PUT    /provider-rate-limits/{provider}                   - Update provider config
 *   GET    /provider-rate-limits/{provider}/tenant/{tenantId} - Tenant override
 *   PUT    /provider-rate-limits/{provider}/tenant/{tenantId} - Update tenant override
 *   DELETE /provider-rate-limits/{provider}/tenant/{tenantId} - Remove tenant override
 *   POST   /provider-rate-limits/{provider}/reset             - Reset counters
 */

import createClient from 'openapi-fetch';
import type { paths } from '@aaimplatform/institutional-types/provider-rate-limits';
import type { components } from '@aaimplatform/institutional-types/provider-rate-limits';
import { authMiddleware } from '../client';

// Inline-create the typed client. F-PORTAL-AUDIENCE-SPLIT (2026-05-17):
// when ProviderRateLimits moved from admin-portal -> platform-admin, the
// dedicated typed-client wasn't part of platform-admin's typed-clients.ts.
// Rather than threading a cross-app symbol, the client is constructed
// here at the only consumption site.
const providerRateLimitsClient = createClient<paths>({ baseUrl: '/api/admin', credentials: 'include' });
providerRateLimitsClient.use(authMiddleware());

export type RateLimitWindow = components['schemas']['RateLimitWindow'];
export type WindowUsage = components['schemas']['WindowUsage'];
export type ProviderRateLimitConfig = components['schemas']['ProviderRateLimitConfig'];
export type ProviderRateLimitListResponse = components['schemas']['ProviderRateLimitListResponse'];
export type TenantProviderRateLimitConfig = components['schemas']['TenantProviderRateLimitConfig'];
export type UpdateProviderRateLimitRequest = components['schemas']['UpdateProviderRateLimitRequest'];
export type UpdateTenantRateLimitRequest = components['schemas']['UpdateTenantRateLimitRequest'];

/**
 * Provider rate limits API -- openapi-fetch typed client.
 */
export const providerRateLimitsApi = {
  /**
   * List all provider rate limit configurations.
   */
  list: async (tenantId?: string): Promise<ProviderRateLimitListResponse> => {
    const { data, error } = await providerRateLimitsClient.GET('/provider-rate-limits', {
      params: {
        query: { tenantId },
      },
    });
    if (error) throw new Error('Failed to fetch provider rate limits');
    return data;
  },

  /**
   * Get rate limit config for a specific provider.
   */
  getProvider: async (provider: string): Promise<ProviderRateLimitConfig> => {
    const { data, error } = await providerRateLimitsClient.GET('/provider-rate-limits/{provider}', {
      params: { path: { provider } },
    });
    if (error) throw new Error('Failed to fetch provider rate limit config');
    return data;
  },

  /**
   * Update platform-level rate limits for a provider.
   */
  updateProvider: async (
    provider: string,
    body: UpdateProviderRateLimitRequest,
  ): Promise<ProviderRateLimitConfig> => {
    const { data, error } = await providerRateLimitsClient.PUT('/provider-rate-limits/{provider}', {
      params: { path: { provider } },
      body,
    });
    if (error) throw new Error('Failed to update provider rate limits');
    return data;
  },

  /**
   * Get tenant-specific overrides for a provider.
   */
  getTenantOverride: async (
    provider: string,
    tenantId: string,
  ): Promise<TenantProviderRateLimitConfig> => {
    const { data, error } = await providerRateLimitsClient.GET(
      '/provider-rate-limits/{provider}/tenant/{tenantId}',
      {
        params: { path: { provider, tenantId } },
      },
    );
    if (error) throw new Error('Failed to fetch tenant rate limit override');
    return data;
  },

  /**
   * Set tenant-specific rate limit overrides.
   */
  updateTenantOverride: async (
    provider: string,
    tenantId: string,
    body: UpdateTenantRateLimitRequest,
  ): Promise<TenantProviderRateLimitConfig> => {
    const { data, error } = await providerRateLimitsClient.PUT(
      '/provider-rate-limits/{provider}/tenant/{tenantId}',
      {
        params: { path: { provider, tenantId } },
        body,
      },
    );
    if (error) throw new Error('Failed to update tenant rate limit override');
    return data;
  },

  /**
   * Remove tenant overrides (revert to platform defaults).
   */
  deleteTenantOverride: async (
    provider: string,
    tenantId: string,
  ): Promise<void> => {
    const { error } = await providerRateLimitsClient.DELETE(
      '/provider-rate-limits/{provider}/tenant/{tenantId}',
      {
        params: { path: { provider, tenantId } },
      },
    );
    if (error) throw new Error('Failed to remove tenant rate limit override');
  },

  /**
   * Reset rate limit counters for a provider.
   */
  resetCounters: async (provider: string): Promise<void> => {
    const { error } = await providerRateLimitsClient.POST(
      '/provider-rate-limits/{provider}/reset',
      {
        params: { path: { provider } },
      },
    );
    if (error) throw new Error('Failed to reset provider rate limit counters');
  },
};
